import { FormikTouched, useFormik } from 'formik';
import * as Yup from 'yup';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, FormHelperText, InputAdornment, InputLabel, MobileStepper, OutlinedInput, SelectChangeEvent, Stack, Typography, useTheme } from '@mui/material';
import moment, { Moment } from 'moment';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { HighlightOff, KeyboardArrowLeft, KeyboardArrowRight, SaveOutlined } from '@mui/icons-material';
import { IArchivedAndReportPeriodDetails } from '../../../../shared/services/report-period/report-period.model';
import TargetSettingService from '../../../../shared/services/target-setting/target-setting.service';
import { IPostArchivedReportPeriod } from '../../../../shared/services/archived-report-period/archived-report-period.model';
import { LoadingButton } from '@mui/lab';
import { ArchivedBreakdownField, ArchivedBreakdownFields, archivedBreakdownErrors } from '../../../presentation-components/settings-components/archived-breakdown-fields';
import { BreakdownInputs, cleanBreakdown } from '../../../../shared/utils/archived-breakdown-utils';

// Steps: 0 period details, 1 scope totals, 2 breakdown by category (optional), 3 energy and offsets.
const CATEGORY_STEP = 2;
const LAST_STEP = 3;

export const AddArchivedReportPeriodDialog: FunctionComponent<{
  open: boolean,
  closeHandler: () => void,
  reportPeriodDetails?: IArchivedAndReportPeriodDetails,
  submitHandler: (values: IPostArchivedReportPeriod, formikResetForm: () => void) => void,
  activeStep: number,
  setActiveStep: React.Dispatch<React.SetStateAction<any>>,
  errorMessage: string,
  setErrorMessage: React.Dispatch<React.SetStateAction<string>>,
  isSubmitting: boolean,
}> = ({
  open,
  closeHandler,
  reportPeriodDetails,
  submitHandler,
  activeStep,
  setActiveStep,
  errorMessage,
  setErrorMessage,
  isSubmitting,
}) => {
    //Services
    const targetSettingService = new TargetSettingService()
    //State
    const [openDate, setOpenDate] = useState(false);
    const [hasReductionPlan, setHasReductionPlan] = useState<boolean>(false)
    //Layout
    const theme = useTheme();

    useEffect(() => {
      targetSettingService.getTargetSettings()
        .then(response => {
          if (response.length > 0) {
            setHasReductionPlan(true)
          }
        })
        .catch(err => console.log(err))
    }, [])

    useEffect(() => {
      setErrorMessage('');
    }, [activeStep, setErrorMessage])

    const archivedReportPeriodSchema = Yup.object().shape({
      friendlyName: Yup.string().required('Name is required'),
      startDate: Yup.string().required('Start date is required'),
      endDate: Yup.string().required('End date is required'),
      baseYear: Yup.boolean(),
      manualEntry: Yup.boolean(),
      totalRevenue: Yup.number().integer('Your total revenue must be a whole number').nullable(),
      currencyCode: Yup.string().nullable(),
      numberOfUkEmployees: Yup.number().nullable(),
      numberOfNonUkEmployees: Yup.number().nullable(),
      totalScopeOneTCEUk: Yup.number().nullable(),
      totalScopeTwoLocationTCEUk: Yup.number().nullable(),
      totalScopeTwoMarketTCEUk: Yup.number().nullable(),
      totalScopeThreeTCEUk: Yup.number().nullable(),
      totalScopeOneTCEGlobal: Yup.number().nullable(),
      totalScopeTwoLocationTCEGlobal: Yup.number().nullable(),
      totalScopeTwoMarketTCEGlobal: Yup.number().nullable(),
      totalScopeThreeTCEGlobal: Yup.number().nullable(),
      totalEnergyConsumptionKwhGlobal: Yup.number().nullable(),
      totalEnergyConsumptionKwhUk: Yup.number().nullable(),
      carbonCreditsTCE: Yup.number().nullable(),
    })

    const formik = useFormik({
      initialValues: {
        id: reportPeriodDetails ? reportPeriodDetails.id : undefined,
        startDate: reportPeriodDetails ? reportPeriodDetails.startDate : '',
        endDate: reportPeriodDetails ? reportPeriodDetails.endDate : '',
        friendlyName: reportPeriodDetails ? reportPeriodDetails.friendlyName : '',
        baseYear: reportPeriodDetails ? reportPeriodDetails.baseYear : false,
        manualEntry: reportPeriodDetails ? reportPeriodDetails.manualEntry : true,
        totalRevenue: reportPeriodDetails ? reportPeriodDetails.totalRevenue : undefined,
        currencyCode: reportPeriodDetails ? reportPeriodDetails.currencyCode : '',
        numberOfUkEmployees: reportPeriodDetails ? reportPeriodDetails.numberOfUkEmployees : undefined,
        numberOfNonUkEmployees: reportPeriodDetails ? reportPeriodDetails.numberOfNonUkEmployees : undefined,
        totalScopeOneTCEUk: reportPeriodDetails ? reportPeriodDetails.totalScopeOneTCEUk : undefined,
        totalScopeTwoLocationTCEUk: reportPeriodDetails ? reportPeriodDetails.totalScopeTwoLocationTCEUk : undefined,
        totalScopeTwoMarketTCEUk: reportPeriodDetails ? reportPeriodDetails.totalScopeTwoMarketTCEUk : undefined,
        totalScopeThreeTCEUk: reportPeriodDetails ? reportPeriodDetails.totalScopeThreeTCEUk : undefined,
        totalScopeOneTCEGlobal: reportPeriodDetails ? reportPeriodDetails.totalScopeOneTCEGlobal : undefined,
        totalScopeTwoLocationTCEGlobal: reportPeriodDetails ? reportPeriodDetails.totalScopeTwoLocationTCEGlobal : undefined,
        totalScopeTwoMarketTCEGlobal: reportPeriodDetails ? reportPeriodDetails.totalScopeTwoMarketTCEGlobal : undefined,
        totalScopeThreeTCEGlobal: reportPeriodDetails ? reportPeriodDetails.totalScopeThreeTCEGlobal : undefined,
        scopeOneGroupsTCEUk: (reportPeriodDetails?.scopeOneGroupsTCEUk ?? {}) as BreakdownInputs,
        scopeOneGroupsTCEGlobal: (reportPeriodDetails?.scopeOneGroupsTCEGlobal ?? {}) as BreakdownInputs,
        scopeThreeCategoriesTCEUk: (reportPeriodDetails?.scopeThreeCategoriesTCEUk ?? {}) as BreakdownInputs,
        scopeThreeCategoriesTCEGlobal: (reportPeriodDetails?.scopeThreeCategoriesTCEGlobal ?? {}) as BreakdownInputs,
        totalEnergyConsumptionKwhGlobal: reportPeriodDetails ? reportPeriodDetails.totalEnergyConsumptionKwhGlobal : undefined,
        totalEnergyConsumptionKwhUk: reportPeriodDetails ? reportPeriodDetails.totalEnergyConsumptionKwhUk : undefined,
        carbonCreditsTCE: reportPeriodDetails ? reportPeriodDetails.carbonCreditsTCE : undefined,
      },
      validationSchema: archivedReportPeriodSchema,
      onSubmit: (values) => {
        // A total lowered after the breakdown was entered: send the user back to fix it.
        if (archivedBreakdownErrors(values).length > 0) {
          setActiveStep(CATEGORY_STEP);
          return;
        }
        const formattedValues = {
          ...handleEmptyStrings(values),
          scopeOneGroupsTCEUk: cleanBreakdown(values.scopeOneGroupsTCEUk),
          scopeOneGroupsTCEGlobal: cleanBreakdown(values.scopeOneGroupsTCEGlobal),
          scopeThreeCategoriesTCEUk: cleanBreakdown(values.scopeThreeCategoriesTCEUk),
          scopeThreeCategoriesTCEGlobal: cleanBreakdown(values.scopeThreeCategoriesTCEGlobal),
        };
        submitHandler(formattedValues, formik.resetForm);
      }
    });

    useEffect(() => {
      if (reportPeriodDetails) {
        formik.setValues({
          id: reportPeriodDetails.id,
          startDate: reportPeriodDetails.startDate,
          endDate: reportPeriodDetails.endDate,
          friendlyName: reportPeriodDetails.friendlyName,
          baseYear: reportPeriodDetails.baseYear,
          manualEntry: reportPeriodDetails.manualEntry,
          totalRevenue: reportPeriodDetails.totalRevenue,
          currencyCode: reportPeriodDetails.currencyCode,
          numberOfUkEmployees: reportPeriodDetails.numberOfUkEmployees,
          numberOfNonUkEmployees: reportPeriodDetails.numberOfNonUkEmployees,
          totalScopeOneTCEUk: reportPeriodDetails.totalScopeOneTCEUk,
          totalScopeTwoLocationTCEUk: reportPeriodDetails.totalScopeTwoLocationTCEUk,
          totalScopeTwoMarketTCEUk: reportPeriodDetails.totalScopeTwoMarketTCEUk,
          totalScopeThreeTCEUk: reportPeriodDetails.totalScopeThreeTCEUk,
          totalScopeOneTCEGlobal: reportPeriodDetails.totalScopeOneTCEGlobal,
          totalScopeTwoLocationTCEGlobal: reportPeriodDetails.totalScopeTwoLocationTCEGlobal,
          totalScopeTwoMarketTCEGlobal: reportPeriodDetails.totalScopeTwoMarketTCEGlobal,
          totalScopeThreeTCEGlobal: reportPeriodDetails.totalScopeThreeTCEGlobal,
          scopeOneGroupsTCEUk: reportPeriodDetails.scopeOneGroupsTCEUk ?? {},
          scopeOneGroupsTCEGlobal: reportPeriodDetails.scopeOneGroupsTCEGlobal ?? {},
          scopeThreeCategoriesTCEUk: reportPeriodDetails.scopeThreeCategoriesTCEUk ?? {},
          scopeThreeCategoriesTCEGlobal: reportPeriodDetails.scopeThreeCategoriesTCEGlobal ?? {},
          totalEnergyConsumptionKwhGlobal: reportPeriodDetails.totalEnergyConsumptionKwhGlobal,
          totalEnergyConsumptionKwhUk: reportPeriodDetails.totalEnergyConsumptionKwhUk,
          carbonCreditsTCE: reportPeriodDetails.carbonCreditsTCE,
        })
      }
    }, [reportPeriodDetails])


    const handleEmptyStrings = (values: any) => {
      const formattedValues = { ...values };
      Object.keys(formattedValues).forEach((key) => {
        if (typeof formattedValues[key] === 'string' && formattedValues[key].trim() === '') {
          formattedValues[key] = null;
        }
      });
      return formattedValues;
    };


    const dateHandler = (date: Moment | null, name: string) => {
      if (date) {
        const updatedReportPeriod = { ...formik.values, [name]: date.format('YYYY-MM-DD') };
        if (name === 'startDate') {
          updatedReportPeriod.endDate = moment(date).add(1, 'year').subtract(1, 'day').format('YYYY-MM-DD');
          updatedReportPeriod.friendlyName = moment(updatedReportPeriod.endDate).year().toString();
        }
        formik.setValues(updatedReportPeriod);
      }
    }

    const breakdownHandler = (field: ArchivedBreakdownField, key: string, value: number | '') => {
      formik.setFieldValue(field, { ...formik.values[field], [key]: value });
    }

    const checkboxHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { checked } = e.target;
      formik.setValues({ ...formik.values, 'baseYear': checked })
    }

    const cancelHandler = () => {
      formik.resetForm();
      closeHandler();
      setActiveStep(0)
      formik.setErrors({})
    }

    const handleNext = async () => {
      const errors = await formik.validateForm();
      const hasErrors = Object.keys(errors).some(
        (errorField) => (formik.values as Record<string, any>)[errorField] !== undefined
      ) || (activeStep === CATEGORY_STEP && archivedBreakdownErrors(formik.values).length > 0);
      if (!hasErrors) {
        setActiveStep((prevActiveStep: number) => prevActiveStep + 1);
        setErrorMessage('');
      } else {
        const touchedFields = Object.keys(errors).reduce((acc, key) => {
          acc[key] = true;
          return acc;
        }, {} as Record<string, boolean>) as FormikTouched<typeof formik.values>;
        formik.setTouched(touchedFields);
      }

      if (activeStep === LAST_STEP) {
        formik.submitForm();
      }
    };

    const handleBack = () => {
      setActiveStep((prevActiveStep: number) => prevActiveStep - 1);
      setErrorMessage('');
    };

    return (
      <Dialog open={open} onClose={closeHandler} maxWidth='sm' fullWidth>
        <Stack display='flex' flexDirection='row' justifyContent='space-between'>
          <DialogTitle sx={{ pt: 2, pl: 4, pr: 0 }}>
            Add Archived Report Period
          </DialogTitle>
          <Button variant='text' onClick={cancelHandler} disabled={isSubmitting}>
            <HighlightOff sx={{ fontSize: '1.25rem', color: 'primary' }} />
          </Button>
        </Stack>
        <Box sx={{ pb: 4, pl: 4, pr: 4 }} component='form'>
          <DialogContent sx={{ pt: 0, pl: 0, pr: 0 }}>
            <Box component='form' sx={{ mt: 1 }}>
              {activeStep === 0 && <>
                {/* <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel htmlFor='friendlyName'>Name</InputLabel>
                  <OutlinedInput inputProps={{ min: 0 }} id='friendlyName' label='Name' name='friendlyName'
                    value={formik.values.friendlyName}
                    onChange={formik.handleChange}
                    error={formik.touched.friendlyName && Boolean(formik.errors.friendlyName)}
                    sx={{ color: 'text.primary' }}></OutlinedInput>
                  {formik.touched.friendlyName && formik.errors.friendlyName &&
                    <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.friendlyName}</FormHelperText>
                  }
                </FormControl> */}
                <Stack display='flex' direction='row' spacing={2} sx={{ mb: 2 }}>
                  <FormControl fullWidth variant='outlined'>
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                      <DatePicker
                        value={formik.values.startDate ? moment(formik.values.startDate) : null}
                        onChange={(date) => dateHandler(date, 'startDate')}
                        format='DD/MM/YYYY'
                        open={openDate}
                        shouldDisableDate={(date) => date.date() !== 1}
                        label='Start Date'
                        sx={{ width: '100%' }}
                        onOpen={() => setOpenDate(true)}
                        onClose={() => setOpenDate(false)}
                        slotProps={{
                          textField: {
                            onClick: () => setOpenDate(true),
                            error: formik.touched.startDate && Boolean(formik.errors.startDate)
                          },
                        }}
                      />
                      {formik.touched.startDate && formik.errors.startDate &&
                        <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.startDate}</FormHelperText>
                      }
                    </LocalizationProvider>
                  </FormControl>
                  <FormControl fullWidth variant='outlined'>
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                      <DatePicker
                        disabled
                        label='End Date'
                        sx={{ width: '100%' }}
                        value={formik.values.endDate ? moment(formik.values.endDate) : null}
                        onChange={(date) => dateHandler(date, 'endDate')}
                        format='DD/MM/YYYY'
                      />
                    </LocalizationProvider>
                  </FormControl>
                </Stack>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel htmlFor='numberOfUkEmployees'>Number of UK Employees</InputLabel>
                  <OutlinedInput inputProps={{ min: 0 }} id='numberOfUkEmployees' label='Number of UK Employees' name='numberOfUkEmployees' type='number'
                    value={formik.values.numberOfUkEmployees}
                    onChange={formik.handleChange}
                    error={formik.touched.numberOfUkEmployees && Boolean(formik.errors.numberOfUkEmployees)}
                    sx={{ color: 'text.primary' }}></OutlinedInput>
                  {formik.touched.numberOfUkEmployees && formik.errors.numberOfUkEmployees &&
                    <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.numberOfUkEmployees}</FormHelperText>
                  }
                </FormControl >
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel htmlFor='numberOfNonUkEmployees'>Number of Non UK Employees</InputLabel>
                  <OutlinedInput inputProps={{ min: 0 }} id='numberOfNonUkEmployees' label='Number of Non UK Employees' name='numberOfNonUkEmployees' type='number'
                    value={formik.values.numberOfNonUkEmployees}
                    onChange={formik.handleChange}
                    error={formik.touched.numberOfNonUkEmployees && Boolean(formik.errors.numberOfNonUkEmployees)}
                    sx={{ color: 'text.primary' }}></OutlinedInput>
                  {formik.touched.numberOfNonUkEmployees && formik.errors.numberOfNonUkEmployees &&
                    <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.numberOfNonUkEmployees}</FormHelperText>
                  }
                </FormControl >
                <Stack display='flex' direction='row' spacing={2} sx={{ mb: 2, width: '100%' }}>
                  <FormControl fullWidth>
                    <InputLabel htmlFor='totalRevenue'>Total Revenue in £</InputLabel>
                    <OutlinedInput inputProps={{ min: 0, step: 1 }} id='totalRevenue' label='Total Revenue in £' name='totalRevenue' type='number'
                      value={formik.values.totalRevenue}
                      onChange={formik.handleChange}
                      error={formik.touched.totalRevenue && Boolean(formik.errors.totalRevenue)}
                      sx={{ color: 'text.primary' }}></OutlinedInput>
                    {formik.touched.totalRevenue && formik.errors.totalRevenue &&
                      <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.totalRevenue}</FormHelperText>
                    }
                  </FormControl>
                  {/* {countries && <FormControl fullWidth variant='outlined'>
                    <Autocomplete
                      id="countryId"
                      options={countries.slice().sort((a, b) => (a.name === 'United Kingdom' ? -1 : b.name === 'United Kingdom' ? 1 : a.name.localeCompare(b.name)))}
                      autoHighlight
                      getOptionLabel={(option) => `${option.name}, ${option.currencySymbol}`}
                      renderOption={(props, option) => {
                        const { key, ...restProps } = props as { key: React.Key } & React.HTMLAttributes<HTMLLIElement>;
                        return (
                          <Box
                            component="li"
                            sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
                            key={key}
                            {...restProps}
                          >
                            <img
                              loading="lazy"
                              width="20"
                              src={option.imageUrl}
                              alt="country flag"
                            />
                            {option.name}, {option.currencySymbol}
                          </Box>
                        )
                      }}
                      value={countries?.find(country => country.currencyCode === formik.values.currencyCode)}
                      onChange={(e: any, value: ICountry | null) => selectCurrencyHandler(e, value)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          error={formik.touched.currencyCode && Boolean(formik.errors.currencyCode)}
                          helperText={formik.touched.currencyCode && formik.errors.currencyCode}
                          placeholder='Currency'
                        />
                      )}
                    />
                  </FormControl>} */}
                </Stack>
                {!reportPeriodDetails && !hasReductionPlan && <FormControlLabel control={<Checkbox onChange={checkboxHandler} checked={formik.values.baseYear} />} label="Record this report period as your base year" />}
              </>}
              {activeStep === 1 && <>
                <Typography variant='subtitle1' sx={{ mb: 1 }}>UK</Typography>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel htmlFor='totalScopeOneTCEUk'>Scope 1</InputLabel>
                  <OutlinedInput inputProps={{ min: 0 }} id='totalScopeOneTCEUk' label='Scope 1' name='totalScopeOneTCEUk' type='number'
                    value={formik.values.totalScopeOneTCEUk}
                    onChange={formik.handleChange}
                    endAdornment={<InputAdornment position="end"><Typography variant='body2' sx={{ color: 'text.secondary' }}>tCO<sub>2</sub>e</Typography></InputAdornment>}
                    error={formik.touched.totalScopeOneTCEUk && Boolean(formik.errors.totalScopeOneTCEUk)}
                    sx={{ color: 'text.primary' }}></OutlinedInput>
                  {formik.touched.totalScopeOneTCEUk && formik.errors.totalScopeOneTCEUk &&
                    <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.totalScopeOneTCEUk}</FormHelperText>
                  }
                </FormControl>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel htmlFor='totalScopeTwoMarketTCEUk'>Scope 2 (Market-based)</InputLabel>
                  <OutlinedInput inputProps={{ min: 0 }} id='totalScopeTwoMarketTCEUk' label='Scope 2 (Market-based)' name='totalScopeTwoMarketTCEUk' type='number'
                    value={formik.values.totalScopeTwoMarketTCEUk}
                    onChange={formik.handleChange}
                    endAdornment={<InputAdornment position="end"><Typography variant='body2' sx={{ color: 'text.secondary' }}>tCO<sub>2</sub>e</Typography></InputAdornment>}
                    error={formik.touched.totalScopeTwoMarketTCEUk && Boolean(formik.errors.totalScopeTwoMarketTCEUk)}
                    sx={{ color: 'text.primary' }}></OutlinedInput>
                  {formik.touched.totalScopeTwoMarketTCEUk && formik.errors.totalScopeTwoMarketTCEUk &&
                    <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.totalScopeTwoMarketTCEUk}</FormHelperText>
                  }
                </FormControl>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel htmlFor='totalScopeTwoLocationTCEUk'>Scope 2 (Location-based)</InputLabel>
                  <OutlinedInput inputProps={{ min: 0 }} id='totalScopeTwoLocationTCEUk' label='Scope 2 (Location-based)' name='totalScopeTwoLocationTCEUk' type='number'
                    value={formik.values.totalScopeTwoLocationTCEUk}
                    onChange={formik.handleChange}
                    endAdornment={<InputAdornment position="end"><Typography variant='body2' sx={{ color: 'text.secondary' }}>tCO<sub>2</sub>e</Typography></InputAdornment>}
                    error={formik.touched.totalScopeTwoLocationTCEUk && Boolean(formik.errors.totalScopeTwoLocationTCEUk)}
                    sx={{ color: 'text.primary' }}></OutlinedInput>
                  {formik.touched.totalScopeTwoLocationTCEUk && formik.errors.totalScopeTwoLocationTCEUk &&
                    <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.totalScopeTwoLocationTCEUk}</FormHelperText>
                  }
                </FormControl>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel htmlFor='totalScopeThreeTCEUk'>Scope 3</InputLabel>
                  <OutlinedInput inputProps={{ min: 0 }} id='totalScopeThreeTCEUk' label='Scope 3' name='totalScopeThreeTCEUk' type='number'
                    value={formik.values.totalScopeThreeTCEUk}
                    onChange={formik.handleChange}
                    endAdornment={<InputAdornment position="end"><Typography variant='body2' sx={{ color: 'text.secondary' }}>tCO<sub>2</sub>e</Typography></InputAdornment>}
                    error={formik.touched.totalScopeThreeTCEUk && Boolean(formik.errors.totalScopeThreeTCEUk)}
                    sx={{ color: 'text.primary' }}></OutlinedInput>
                  {formik.touched.totalScopeThreeTCEUk && formik.errors.totalScopeThreeTCEUk &&
                    <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.totalScopeThreeTCEUk}</FormHelperText>
                  }
                </FormControl>
                <Typography variant='subtitle1' sx={{mb:1}}>Overseas</Typography>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel htmlFor='totalScopeOneTCEGlobal'>Scope 1</InputLabel>
                  <OutlinedInput inputProps={{ min: 0 }} id='totalScopeOneTCEGlobal' label='Scope 1' name='totalScopeOneTCEGlobal' type='number'
                    value={formik.values.totalScopeOneTCEGlobal}
                    onChange={formik.handleChange}
                    endAdornment={<InputAdornment position="end"><Typography variant='body2' sx={{ color: 'text.secondary' }}>tCO<sub>2</sub>e</Typography></InputAdornment>}
                    error={formik.touched.totalScopeOneTCEGlobal && Boolean(formik.errors.totalScopeOneTCEGlobal)}
                    sx={{ color: 'text.primary' }}></OutlinedInput>
                  {formik.touched.totalScopeOneTCEGlobal && formik.errors.totalScopeOneTCEGlobal &&
                    <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.totalScopeOneTCEGlobal}</FormHelperText>
                  }
                </FormControl>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel htmlFor='totalScopeTwoMarketTCEGlobal'>Scope 2 (Market-based)</InputLabel>
                  <OutlinedInput inputProps={{ min: 0 }} id='totalScopeTwoMarketTCEGlobal' label='Scope 2 (Market-based)' name='totalScopeTwoMarketTCEGlobal' type='number'
                    value={formik.values.totalScopeTwoMarketTCEGlobal}
                    onChange={formik.handleChange}
                    endAdornment={<InputAdornment position="end"><Typography variant='body2' sx={{ color: 'text.secondary' }}>tCO<sub>2</sub>e</Typography></InputAdornment>}
                    error={formik.touched.totalScopeTwoMarketTCEGlobal && Boolean(formik.errors.totalScopeTwoMarketTCEGlobal)}
                    sx={{ color: 'text.primary' }}></OutlinedInput>
                  {formik.touched.totalScopeTwoMarketTCEGlobal && formik.errors.totalScopeTwoMarketTCEGlobal &&
                    <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.totalScopeTwoMarketTCEGlobal}</FormHelperText>
                  }
                </FormControl>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel htmlFor='totalScopeTwoLocationTCEGlobal'>Scope 2 (Location-based)</InputLabel>
                  <OutlinedInput inputProps={{ min: 0 }} id='totalScopeTwoLocationTCEGlobal' label='Scope 2 (Location-based)' name='totalScopeTwoLocationTCEGlobal' type='number'
                    value={formik.values.totalScopeTwoLocationTCEGlobal}
                    onChange={formik.handleChange}
                    endAdornment={<InputAdornment position="end"><Typography variant='body2' sx={{ color: 'text.secondary' }}>tCO<sub>2</sub>e</Typography></InputAdornment>}
                    error={formik.touched.totalScopeTwoLocationTCEGlobal && Boolean(formik.errors.totalScopeTwoLocationTCEGlobal)}
                    sx={{ color: 'text.primary' }}></OutlinedInput>
                  {formik.touched.totalScopeTwoLocationTCEGlobal && formik.errors.totalScopeTwoLocationTCEGlobal &&
                    <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.totalScopeTwoLocationTCEGlobal}</FormHelperText>
                  }
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel htmlFor='totalScopeThreeTCEGlobal'>Scope 3</InputLabel>
                  <OutlinedInput inputProps={{ min: 0 }} id='totalScopeThreeTCEGlobal' label='Scope 3' name='totalScopeThreeTCEGlobal' type='number'
                    value={formik.values.totalScopeThreeTCEGlobal}
                    onChange={formik.handleChange}
                    endAdornment={<InputAdornment position="end"><Typography variant='body2' sx={{ color: 'text.secondary' }}>tCO<sub>2</sub>e</Typography></InputAdornment>}
                    error={formik.touched.totalScopeThreeTCEGlobal && Boolean(formik.errors.totalScopeThreeTCEGlobal)}
                    sx={{ color: 'text.primary' }}></OutlinedInput>
                  {formik.touched.totalScopeThreeTCEGlobal && formik.errors.totalScopeThreeTCEGlobal &&
                    <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.totalScopeThreeTCEGlobal}</FormHelperText>
                  }
                </FormControl>
              </>}
              {activeStep === CATEGORY_STEP && (
                <ArchivedBreakdownFields values={formik.values} onChange={breakdownHandler} />
              )}
              {activeStep === LAST_STEP && <>
                <Typography variant='body2' sx={{ fontWeight: 'bold', color: 'text.secondary', mb: 2 }}>Energy consumption is the total energy from all SECR Scope 1 and Scope 2 activities, converted to kWh.</Typography>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel htmlFor='totalEnergyConsumptionKwhUk'>Total Energy Consumption UK</InputLabel>
                  <OutlinedInput inputProps={{ min: 0 }} id='totalEnergyConsumptionKwhUk' label='Total Energy Consumption UK' name='totalEnergyConsumptionKwhUk' type='number'
                    value={formik.values.totalEnergyConsumptionKwhUk}
                    onChange={formik.handleChange}
                    endAdornment={<InputAdornment position="end"><Typography variant='body2' sx={{ color: 'text.secondary' }}>kWh</Typography></InputAdornment>}
                    error={formik.touched.totalEnergyConsumptionKwhUk && Boolean(formik.errors.totalEnergyConsumptionKwhUk)}
                    sx={{ color: 'text.primary' }}></OutlinedInput>
                  {formik.touched.totalEnergyConsumptionKwhUk && formik.errors.totalEnergyConsumptionKwhUk &&
                    <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.totalEnergyConsumptionKwhUk}</FormHelperText>
                  }
                </FormControl>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel htmlFor='totalEnergyConsumptionKwhGlobal'>Total Energy Consumption Overseas</InputLabel>
                  <OutlinedInput inputProps={{ min: 0 }} id='totalEnergyConsumptionKwhGlobal' label='Total Energy Consumption Overseas' name='totalEnergyConsumptionKwhGlobal' type='number'
                    value={formik.values.totalEnergyConsumptionKwhGlobal}
                    endAdornment={<InputAdornment position="end"><Typography variant='body2' sx={{ color: 'text.secondary' }}>kWh</Typography></InputAdornment>}
                    onChange={formik.handleChange}
                    error={formik.touched.totalEnergyConsumptionKwhGlobal && Boolean(formik.errors.totalEnergyConsumptionKwhGlobal)}
                    sx={{ color: 'text.primary' }}></OutlinedInput>
                  {formik.touched.totalEnergyConsumptionKwhGlobal && formik.errors.totalEnergyConsumptionKwhGlobal &&
                    <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.totalEnergyConsumptionKwhGlobal}</FormHelperText>
                  }
                </FormControl>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel htmlFor='carbonCreditsTCE'>Total Offsets</InputLabel>
                  <OutlinedInput inputProps={{ min: 0 }} id='carbonCreditsTCE' label='Total Offsets' name='carbonCreditsTCE' type='number'
                    value={formik.values.carbonCreditsTCE}
                    onChange={formik.handleChange}
                    endAdornment={<InputAdornment position="end"><Typography variant='body2' sx={{ color: 'text.secondary' }}>tCO<sub>2</sub>e</Typography></InputAdornment>}
                    error={formik.touched.carbonCreditsTCE && Boolean(formik.errors.carbonCreditsTCE)}
                    sx={{ color: 'text.primary' }}></OutlinedInput>
                  {formik.touched.carbonCreditsTCE && formik.errors.carbonCreditsTCE &&
                    <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.carbonCreditsTCE}</FormHelperText>
                  }
                </FormControl>
                {errorMessage &&
                  <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important", mb: 2 }}>{errorMessage}
                  </FormHelperText>
                }
              </>}
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 0 }}>
            <MobileStepper
              sx={{ width: '100%', flexGrow: 1 }}
              variant="dots"
              steps={LAST_STEP + 1}
              position="static"
              activeStep={activeStep}
              nextButton={activeStep !== LAST_STEP ?
                <Button onClick={handleNext}>
                  <Typography> Next</Typography>
                  {theme.direction === 'rtl' ? (
                    <KeyboardArrowLeft />
                  ) : (
                    <KeyboardArrowRight />
                  )}
                </Button> :
                <LoadingButton
                  onClick={formik.submitForm}
                  disabled={isSubmitting}
                  startIcon={<SaveOutlined />}
                  variant='outlined'
                  loading={isSubmitting}
                  loadingPosition="start">
                  <Typography>{isSubmitting ? 'Saving' : 'Save'}</Typography>
                </LoadingButton>
              }
              backButton={
                <Button onClick={handleBack} disabled={activeStep === 0 || isSubmitting}>
                  {theme.direction === 'rtl' ? (
                    <KeyboardArrowRight />
                  ) : (
                    <KeyboardArrowLeft />
                  )}
                  <Typography> Back</Typography>
                </Button>
              }
            />
          </DialogActions>
        </Box>
      </Dialog >
    )
  }